// src/hooks/useDevLogging.js
import { useRef, useEffect } from 'react';
import { isDebugMode } from '../utils/debugUtils';

/**
 * Custom hook for advanced component logging in development mode.
 * Logs render count, timing, data snapshots, prop changes, and a summary after activity stops.
 *
 * @param {string} componentName - Name of the component being logged
 * @param {Object} [data] - Optional: State or Props to log (e.g., { user, properties })
 */
export const useDevLogging = (componentName, data = {}) => {
  const renderCount = useRef(0);
  const lastRenderTime = useRef(Date.now());
  const prevData = useRef(data);

  // History for the summary
  const renderHistory = useRef([]);
  const debounceTimeout = useRef(null);

  // We remove the dependency array so this runs after EVERY render
  useEffect(() => {
    // 1. Check if Debug Mode is active
    if (!isDebugMode()) return;

    const now = Date.now();
    const timeSinceLast = now - lastRenderTime.current;
    renderCount.current += 1;

    // 2. Calculate Changes
    const changes = [];
    // Only compare if it's not the first render
    if (renderCount.current > 1) {
      const currentKeys = Object.keys(data);
      const prevKeys = Object.keys(prevData.current);
      // Create a Set of unique keys from both current and previous data
      const allKeys = new Set([...currentKeys, ...prevKeys]);

      allKeys.forEach((key) => {
        // Simple reference equality check
        if (prevData.current[key] !== data[key]) {
          changes.push({
            key,
            from: prevData.current[key],
            to: data[key],
          });
        }
      });
    }

    // 3. Determine Icon and Color based on activity
    const isMount = renderCount.current === 1;
    const hasChanges = changes.length > 0;

    let color = '#9E9E9E'; // Gray (Neutral/No Change)
    let icon = '⚪'; // White circle

    if (isMount) {
      color = '#4CAF50'; // Green (Mount)
      icon = '🟢';
    } else if (hasChanges) {
      color = '#2196F3'; // Blue (Update with Data Change)
      icon = '🔵';
    }

    // 4. Log the Main Group Header
    // We keep this collapsed to avoid clutter, but distinct enough to scan
    console.groupCollapsed(`%c${icon} ${componentName} #${renderCount.current} (+${timeSinceLast}ms)${hasChanges ? ' [UPDATED]' : ''}`, `color: ${color}; font-weight: bold;`);

    // 5. Log Data Snapshot (Only if data was provided)
    if (Object.keys(data).length > 0) {
      console.log('📸 Data Snapshot:', data);
    }

    // 6. Log Changes Table or No Changes Message
    if (hasChanges) {
      console.table(changes);
    } else if (!isMount) {
      console.log('Info: Render triggered, but tracked data is identical (Reconciliation).');
    }

    console.groupEnd();

    // 7. Record History for Summary
    renderHistory.current.push({
      '#': renderCount.current,
      'Time (ms)': timeSinceLast,
      Type: isMount ? 'Mount' : hasChanges ? 'Update' : 'Re-render',
      'Changed Keys': hasChanges ? changes.map((c) => c.key).join(', ') : '-',
    });

    // 8. Schedule/Debounce Summary Log
    // If no new renders occur within 2000ms, print the summary
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => {
      if (renderHistory.current.length > 0) {
        console.group(`📊 ${componentName} RENDER SUMMARY (Total: ${renderCount.current})`);
        console.table(renderHistory.current);
        console.groupEnd();

        // Optional: Clear history after printing so next batch is fresh?
        // renderHistory.current = [];
      }
    }, 2000);

    // 9. Update Refs for next pass
    lastRenderTime.current = now;
    prevData.current = data;
  });
};
